import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import {
  CalendarDays, Plus, Trash2, Send, ChevronLeft, ChevronRight,
  Copy, ClipboardPaste, Clock, GripVertical, AlertTriangle, Check, Save
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getWeekDates = (weekStr) => {
  try {
    const [year, week] = weekStr.split('-W').length === 2
      ? [parseInt(weekStr.split('-W')[0]), parseInt(weekStr.split('-W')[1])]
      : [parseInt(weekStr.split('-')[0]), parseInt(weekStr.split('W')[1] || weekStr.split('-')[1])];
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      dates.push(`${yyyy}-${mm}-${dd}`);
    }
    return dates;
  } catch {
    return [];
  }
};

const getISOWeekString = (date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

const getCurrentWeek = () => getISOWeekString(new Date());

const getAdjacentWeek = (weekStr, direction) => {
  const dates = getWeekDates(weekStr);
  if (!dates.length) return weekStr;
  const [y, m, d] = dates[0].split('-').map(Number);
  const ref = new Date(y, m - 1, d + direction * 7, 12, 0, 0);
  return getISOWeekString(ref);
};

const joursSemaine = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const ABSENCE_TYPES = [
  { value: 'CP', label: 'Congés payés' },
  { value: 'RTT', label: 'RTT' },
  { value: 'maladie', label: 'Maladie' },
  { value: 'sans_solde', label: 'Sans solde' },
  { value: 'formation', label: 'Formation' },
  { value: 'evenement_familial', label: 'Événement familial' },
  { value: 'autre', label: 'Autre' }
];

const calcEffective = (debut, fin, pauseMin) => {
  if (!debut || !fin) return 0;
  const [h1, m1] = debut.split(':').map(Number);
  const [h2, m2] = fin.split(':').map(Number);
  const totalMin = (h2 * 60 + m2) - (h1 * 60 + m1) - (pauseMin || 0);
  return Math.max(0, Math.round((totalMin / 60) * 100) / 100);
};

const formatHours = (h) => {
  if (h === 0) return '0h';
  const hours = Math.floor(h);
  const minutes = Math.round((h - hours) * 60);
  return minutes > 0 ? `${hours}h${String(minutes).padStart(2, '0')}` : `${hours}h`;
};

export default function Planning() {
  const { user } = useAuth();
  const canEdit = ['admin', 'rh', 'super_admin'].includes(user?.role);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [contractMap, setContractMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [currentPlanning, setCurrentPlanning] = useState(null);
  const [saving, setSaving] = useState(false);

  // Popover editing
  const [editingCell, setEditingCell] = useState(null); // { employeeId, date }
  const [cellForm, setCellForm] = useState({
    heure_debut: '08:00', heure_fin: '17:00', pause_minutes: 60,
    poste: '', notes: '', isAbsence: false, type_absence: 'CP', heures_absence: 7
  });

  // Clipboard & drag
  const [clipboardSlot, setClipboardSlot] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Duplicate dialog
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateDirection, setDuplicateDirection] = useState(null); // 'prev' or 'next'

  // Creneau shortcuts
  const [creneauShortcuts, setCreneauShortcuts] = useState([]);

  const weekDates = getWeekDates(selectedWeek);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [planningsRes, employeesRes, contratsRes, shortcutsRes] = await Promise.all([
        axios.get(`${API_URL}/planning`, { params: { semaine: selectedWeek } }),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/contrats/actifs`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/creneau-shortcuts`).catch(() => ({ data: [] }))
      ]);
      setCreneauShortcuts(shortcutsRes.data);

      const activeEmps = employeesRes.data.filter(e => e.statut === 'actif');
      setEmployees(activeEmps);

      const deptList = activeEmps.map(e => e.departement).filter((v, i, a) => v && a.indexOf(v) === i);
      setDepartments(deptList);

      // Build contract map: employee_id -> contrat
      const cMap = {};
      for (const c of contratsRes.data) {
        cMap[c.employee_id] = c;
      }
      setContractMap(cMap);

      // Use first existing planning or auto-create
      if (planningsRes.data.length > 0) {
        setCurrentPlanning(planningsRes.data[0]);
      } else if (canEdit) {
        const weekNum = selectedWeek.split('-W')[1];
        const year = selectedWeek.split('-W')[0];
        const res = await axios.post(`${API_URL}/planning`, {
          nom: `Planning S${weekNum} ${year}`,
          semaine: selectedWeek,
          creneaux: []
        });
        setCurrentPlanning(res.data);
      } else {
        setCurrentPlanning(null);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des plannings');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, canEdit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setClipboardSlot(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const changeWeek = (direction) => {
    setSelectedWeek(getAdjacentWeek(selectedWeek, direction));
  };

  const getEmployeeDureeHebdo = (empId) => {
    const contrat = contractMap[empId];
    return contrat?.duree_hebdo || 35;
  };

  const getCreneaux = () => currentPlanning?.creneaux || [];

  const getSlot = (empId, date) => {
    return getCreneaux().find(c => c.employee_id === empId && c.date === date);
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.prenom} ${emp.nom}` : empId;
  };

  // Save creneaux to backend
  const saveCreneaux = async (newCreneaux) => {
    if (!currentPlanning) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/planning/${currentPlanning.id}`, { creneaux: newCreneaux });
      setCurrentPlanning(prev => ({ ...prev, creneaux: newCreneaux }));
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Open popover for cell
  const openCellEditor = (employeeId, date) => {
    if (!canEdit || currentPlanning?.statut === 'publie') return;
    const existing = getSlot(employeeId, date);
    const dureeHebdo = getEmployeeDureeHebdo(employeeId);
    if (existing) {
      setCellForm({
        heure_debut: existing.heure_debut || '08:00',
        heure_fin: existing.heure_fin || '17:00',
        pause_minutes: existing.pause_minutes ?? 60,
        poste: existing.poste || '',
        notes: existing.notes || '',
        isAbsence: existing.type === 'absence',
        type_absence: existing.type_absence || 'CP',
        heures_absence: existing.heures_absence ?? Math.round((dureeHebdo / 5) * 100) / 100
      });
    } else {
      setCellForm({
        heure_debut: '08:00', heure_fin: '17:00', pause_minutes: 60,
        poste: '', notes: '', isAbsence: false, type_absence: 'CP',
        heures_absence: Math.round((dureeHebdo / 5) * 100) / 100
      });
    }
    setEditingCell({ employeeId, date });
  };

  const handleSaveSlot = async () => {
    if (!editingCell || !currentPlanning) return;
    const { employeeId, date } = editingCell;
    const filtered = getCreneaux().filter(c => !(c.employee_id === employeeId && c.date === date));

    const newSlot = {
      employee_id: employeeId,
      date,
      type: cellForm.isAbsence ? 'absence' : 'travail',
      heure_debut: cellForm.isAbsence ? null : cellForm.heure_debut,
      heure_fin: cellForm.isAbsence ? null : cellForm.heure_fin,
      pause_minutes: cellForm.isAbsence ? 0 : cellForm.pause_minutes,
      heures_effectives: cellForm.isAbsence ? 0 : calcEffective(cellForm.heure_debut, cellForm.heure_fin, cellForm.pause_minutes),
      type_absence: cellForm.isAbsence ? cellForm.type_absence : null,
      heures_absence: cellForm.isAbsence ? cellForm.heures_absence : 0,
      poste: cellForm.isAbsence ? '' : cellForm.poste,
      notes: cellForm.notes
    };

    filtered.push(newSlot);
    await saveCreneaux(filtered);
    setEditingCell(null);
  };

  const handleDeleteSlot = async () => {
    if (!editingCell || !currentPlanning) return;
    const { employeeId, date } = editingCell;
    const filtered = getCreneaux().filter(c => !(c.employee_id === employeeId && c.date === date));
    await saveCreneaux(filtered);
    setEditingCell(null);
  };

  const handleCopySlot = () => {
    if (!editingCell) return;
    const slot = getSlot(editingCell.employeeId, editingCell.date);
    if (slot) {
      setClipboardSlot({ ...slot });
      toast.success('Créneau copié');
    }
    setEditingCell(null);
  };

  const handlePaste = async (employeeId, date) => {
    if (!clipboardSlot || !currentPlanning) return;
    const existing = getSlot(employeeId, date);
    if (existing) {
      toast.error('Cette cellule est déjà occupée');
      return;
    }
    const newCreneaux = [...getCreneaux()];
    newCreneaux.push({ ...clipboardSlot, employee_id: employeeId, date });
    await saveCreneaux(newCreneaux);
  };

  // Drag & Drop
  const handleDragStart = (e, empId, date) => {
    if (currentPlanning?.statut === 'publie') return;
    setDragSource({ employeeId: empId, date });
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e, empId, date) => {
    e.preventDefault();
    setDragOver({ employeeId: empId, date });
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = async (e, targetEmpId, targetDate) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragSource || !currentPlanning) return;
    if (dragSource.employeeId === targetEmpId && dragSource.date === targetDate) {
      setDragSource(null);
      return;
    }
    const slot = getSlot(dragSource.employeeId, dragSource.date);
    if (!slot) { setDragSource(null); return; }

    const existingTarget = getSlot(targetEmpId, targetDate);
    if (existingTarget) {
      toast.error('Cette cellule est déjà occupée');
      setDragSource(null);
      return;
    }

    let newCreneaux = [...getCreneaux()];
    newCreneaux.push({ ...slot, employee_id: targetEmpId, date: targetDate });
    await saveCreneaux(newCreneaux);
    setDragSource(null);
  };

  // Duplicate from previous/next week
  const handleDuplicateWeek = async (direction) => {
    if (!currentPlanning) return;
    const sourceWeek = getAdjacentWeek(selectedWeek, direction === 'prev' ? -1 : 1);
    try {
      const res = await axios.get(`${API_URL}/planning`, { params: { semaine: sourceWeek } });
      if (res.data.length === 0) {
        toast.error(`Aucun planning trouvé pour la semaine ${sourceWeek.split('-W')[1]}`);
        return;
      }
      const sourceCreneaux = res.data[0].creneaux || [];
      if (sourceCreneaux.length === 0) {
        toast.error('Le planning source est vide');
        return;
      }
      // Map source dates to current week dates
      const sourceDates = getWeekDates(sourceWeek);
      const targetDates = weekDates;
      const dateMap = {};
      sourceDates.forEach((sd, i) => { if (targetDates[i]) dateMap[sd] = targetDates[i]; });

      const newCreneaux = [...getCreneaux()];
      for (const c of sourceCreneaux) {
        const newDate = dateMap[c.date];
        if (!newDate) continue;
        // Don't overwrite existing slots
        if (!newCreneaux.find(x => x.employee_id === c.employee_id && x.date === newDate)) {
          newCreneaux.push({ ...c, date: newDate });
        }
      }
      await saveCreneaux(newCreneaux);
      toast.success(`Créneaux dupliqués depuis la semaine ${sourceWeek.split('-W')[1]}`);
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
    setIsDuplicateDialogOpen(false);
  };

  // Publish / Unpublish
  const handlePublish = async () => {
    if (!currentPlanning) return;
    try {
      await axios.post(`${API_URL}/planning/${currentPlanning.id}/publish`);
      setCurrentPlanning(prev => ({ ...prev, statut: 'publie' }));
      toast.success('Planning publié');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    }
  };

  const handleUnpublish = async () => {
    if (!currentPlanning) return;
    try {
      await axios.post(`${API_URL}/planning/${currentPlanning.id}/unpublish`);
      setCurrentPlanning(prev => ({ ...prev, statut: 'brouillon' }));
      toast.success('Planning repassé en brouillon');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Calculations
  const getEmployeeWeekTotal = (empId) => {
    const creneaux = getCreneaux().filter(c => c.employee_id === empId);
    let totalTravail = 0;
    let totalAbsence = 0;
    for (const c of creneaux) {
      if (c.type === 'absence') {
        totalAbsence += c.heures_absence || 0;
      } else {
        totalTravail += c.heures_effectives || calcEffective(c.heure_debut, c.heure_fin, c.pause_minutes || 0);
      }
    }
    return { totalTravail, totalAbsence, total: totalTravail + totalAbsence };
  };

  const getEmployeeHeuresSup = (empId) => {
    const { totalTravail } = getEmployeeWeekTotal(empId);
    const dureeHebdo = getEmployeeDureeHebdo(empId);
    return Math.max(0, Math.round((totalTravail - dureeHebdo) * 100) / 100);
  };

  const filteredEmployees = filterDepartment === 'all'
    ? employees
    : employees.filter(e => e.departement === filterDepartment);

  const globalHeuresSup = filteredEmployees.reduce((acc, emp) => acc + getEmployeeHeuresSup(emp.id), 0);

  const isPublished = currentPlanning?.statut === 'publie';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-[#0F5156]" />
            Planning - Semaine {selectedWeek.split('-W')[1]}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {weekDates.length >= 2 && `Du ${weekDates[0]} au ${weekDates[6]}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {saving && <span className="text-sm text-slate-400 flex items-center gap-1"><Save className="h-3 w-3 animate-pulse" /> Sauvegarde...</span>}
          {currentPlanning && (
            <Badge className={isPublished ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
              {isPublished ? 'Publié' : 'Brouillon'}
            </Badge>
          )}
          {globalHeuresSup > 0 && (
            <Badge className="bg-orange-100 text-orange-800">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {formatHours(globalHeuresSup)} sup. total
            </Badge>
          )}
        </div>
      </div>

      {/* Navigation + Actions */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col sm:flex-row items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => changeWeek(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input type="week" value={selectedWeek} onChange={e => { if (e.target.value) setSelectedWeek(e.target.value); }} max="2099-W52" className="w-44" />
              <Button variant="outline" size="sm" onClick={() => changeWeek(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les départements</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 ml-auto">
              {canEdit && currentPlanning && (
                <>
                  <Button variant="outline" size="sm" onClick={() => handleDuplicateWeek('prev')} title="Dupliquer depuis la semaine précédente">
                    <Copy className="h-4 w-4 mr-1" /> S. précédente
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDuplicateWeek('next')} title="Dupliquer depuis la semaine suivante">
                    <Copy className="h-4 w-4 mr-1" /> S. suivante
                  </Button>
                  {isPublished ? (
                    <Button variant="outline" size="sm" onClick={handleUnpublish}>
                      Repasser en brouillon
                    </Button>
                  ) : (
                    <Button size="sm" onClick={handlePublish} className="bg-green-600 hover:bg-green-700">
                      <Send className="h-4 w-4 mr-1" /> Publier
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Published banner */}
      {isPublished && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-700 flex items-center gap-2">
          <Check className="h-4 w-4" />
          Ce planning est publié. Repassez-le en brouillon pour le modifier.
        </div>
      )}

      {/* Clipboard indicator */}
      {clipboardSlot && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-2 text-sm text-violet-700 flex items-center gap-2">
          <ClipboardPaste className="h-4 w-4" />
          Créneau copié — cliquez sur l'icône coller d'une cellule vide (Échap pour annuler)
          <Button variant="ghost" size="sm" onClick={() => setClipboardSlot(null)} className="ml-auto text-violet-500 hover:text-violet-700 h-6 px-2">
            Annuler
          </Button>
        </div>
      )}

      {/* Grid */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-left font-semibold min-w-[160px]">Employé</th>
                {weekDates.map((date, i) => (
                  <th key={date} className="px-1 py-2 text-center font-semibold min-w-[110px]">
                    <div>{joursSemaine[i]}</div>
                    <div className="text-xs text-slate-400 font-normal">{date.slice(5)}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-semibold min-w-[70px] bg-slate-100">Total</th>
                <th className="px-2 py-2 text-center font-semibold min-w-[70px] bg-slate-100">Contrat</th>
                <th className="px-2 py-2 text-center font-semibold min-w-[70px] bg-slate-100">H. Sup</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan={weekDates.length + 4} className="text-center py-8 text-slate-400">
                    Aucun employé actif{filterDepartment !== 'all' ? ' dans ce département' : ''}
                  </td>
                </tr>
              ) : filteredEmployees.map(emp => {
                const { totalTravail, totalAbsence, total } = getEmployeeWeekTotal(emp.id);
                const dureeHebdo = getEmployeeDureeHebdo(emp.id);
                const heuresSup = getEmployeeHeuresSup(emp.id);

                return (
                  <tr key={emp.id} className="border-b hover:bg-slate-50/50">
                    <td className="sticky left-0 bg-white z-10 px-3 py-1 font-medium text-slate-700 border-r">
                      <div>{emp.prenom} {emp.nom}</div>
                      <div className="text-xs text-slate-400">{emp.poste || emp.departement || ''}</div>
                    </td>
                    {weekDates.map(date => {
                      const slot = getSlot(emp.id, date);
                      const isDragTarget = dragOver?.employeeId === emp.id && dragOver?.date === date;
                      const isEditing = editingCell?.employeeId === emp.id && editingCell?.date === date;

                      return (
                        <td
                          key={date}
                          className={`px-1 py-1 text-center align-top border-r transition-colors ${isDragTarget ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
                          onDragOver={(e) => handleDragOver(e, emp.id, date)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, emp.id, date)}
                        >
                          {slot ? (
                            <Popover open={isEditing} onOpenChange={(open) => { if (!open) setEditingCell(null); }}>
                              <PopoverTrigger asChild>
                                <div
                                  draggable={canEdit && !isPublished}
                                  onDragStart={(e) => handleDragStart(e, emp.id, date)}
                                  onClick={() => openCellEditor(emp.id, date)}
                                  className={`cursor-pointer rounded px-1 py-1 text-xs transition-all ${
                                    slot.type === 'absence'
                                      ? 'bg-orange-100 text-orange-800 border border-orange-200'
                                      : 'bg-[#0F5156]/10 text-[#0F5156] border border-[#0F5156]/20'
                                  } ${canEdit && !isPublished ? 'hover:ring-2 hover:ring-[#0F5156]/30' : ''}`}
                                >
                                  {slot.type === 'absence' ? (
                                    <>
                                      <div className="font-medium">{ABSENCE_TYPES.find(a => a.value === slot.type_absence)?.label || slot.type_absence}</div>
                                      <div className="text-[10px]">{formatHours(slot.heures_absence || 0)}</div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="font-medium">{slot.heure_debut}-{slot.heure_fin}</div>
                                      <div className="text-[10px]">{formatHours(slot.heures_effectives || calcEffective(slot.heure_debut, slot.heure_fin, slot.pause_minutes || 0))}</div>
                                      {slot.poste && <div className="text-[10px] text-slate-500 truncate">{slot.poste}</div>}
                                    </>
                                  )}
                                </div>
                              </PopoverTrigger>
                              {isEditing && (
                                <PopoverContent className="w-72 p-3" align="start">
                                  <SlotEditor
                                    form={cellForm}
                                    setForm={setCellForm}
                                    onSave={handleSaveSlot}
                                    onDelete={handleDeleteSlot}
                                    onCopy={handleCopySlot}
                                    isExisting={true}
                                    dureeHebdo={dureeHebdo}
                                    shortcuts={creneauShortcuts}
                                  />
                                </PopoverContent>
                              )}
                            </Popover>
                          ) : (
                            <Popover open={isEditing} onOpenChange={(open) => { if (!open) setEditingCell(null); }}>
                              <PopoverTrigger asChild>
                                <div
                                  onClick={() => openCellEditor(emp.id, date)}
                                  onDragOver={(e) => handleDragOver(e, emp.id, date)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, emp.id, date)}
                                  className={`h-10 flex items-center justify-center rounded cursor-pointer transition-all ${
                                    canEdit && !isPublished ? 'hover:bg-slate-100' : ''
                                  } ${clipboardSlot && canEdit && !isPublished ? 'bg-violet-50 hover:bg-violet-100' : ''}`}
                                >
                                  {clipboardSlot && canEdit && !isPublished ? (
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handlePaste(emp.id, date); }}
                                        className="p-0.5 rounded hover:bg-violet-200 transition-colors"
                                        title="Coller le créneau"
                                      >
                                        <ClipboardPaste className="h-3.5 w-3.5 text-violet-500" />
                                      </button>
                                    </div>
                                  ) : canEdit && !isPublished ? (
                                    <Plus className="h-3.5 w-3.5 text-slate-300" />
                                  ) : (
                                    <span className="text-slate-200">-</span>
                                  )}
                                </div>
                              </PopoverTrigger>
                              {isEditing && (
                                <PopoverContent className="w-72 p-3" align="start">
                                  <SlotEditor
                                    form={cellForm}
                                    setForm={setCellForm}
                                    onSave={handleSaveSlot}
                                    onDelete={null}
                                    onCopy={null}
                                    isExisting={false}
                                    dureeHebdo={dureeHebdo}
                                    shortcuts={creneauShortcuts}
                                  />
                                </PopoverContent>
                              )}
                            </Popover>
                          )}
                        </td>
                      );
                    })}
                    {/* Summary columns */}
                    <td className="px-2 py-1 text-center font-medium bg-slate-50 border-r">
                      <div>{formatHours(total)}</div>
                      {totalAbsence > 0 && <div className="text-[10px] text-orange-600">({formatHours(totalAbsence)} abs.)</div>}
                    </td>
                    <td className="px-2 py-1 text-center text-slate-500 bg-slate-50 border-r">
                      {formatHours(dureeHebdo)}
                    </td>
                    <td className={`px-2 py-1 text-center font-medium bg-slate-50 ${heuresSup > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {heuresSup > 0 ? `+${formatHours(heuresSup)}` : <Check className="h-4 w-4 mx-auto text-green-500" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// Inline slot editor component
function SlotEditor({ form, setForm, onSave, onDelete, onCopy, isExisting, dureeHebdo, shortcuts = [] }) {
  const heuresEffectives = form.isAbsence ? 0 : calcEffective(form.heure_debut, form.heure_fin, form.pause_minutes);

  const applyShortcut = (s) => {
    setForm(prev => ({
      ...prev,
      heure_debut: s.heure_debut,
      heure_fin: s.heure_fin,
      pause_minutes: s.pause_minutes || 60,
      poste: s.poste || prev.poste,
      isAbsence: false
    }));
  };

  return (
    <div className="space-y-3">
      {/* Shortcuts */}
      {shortcuts.length > 0 && !form.isAbsence && (
        <div className="flex flex-wrap gap-1">
          {shortcuts.map(s => (
            <button
              key={s.id}
              onClick={() => applyShortcut(s)}
              className="px-2 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
              title={`${s.heure_debut}-${s.heure_fin} (${s.pause_minutes}min pause)`}
            >
              {s.nom}
            </button>
          ))}
        </div>
      )}

      {/* Absence toggle */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Absence</Label>
        <Switch
          checked={form.isAbsence}
          onCheckedChange={(v) => {
            setForm(prev => ({
              ...prev,
              isAbsence: v,
              heures_absence: v ? Math.round((dureeHebdo / 5) * 100) / 100 : 0
            }));
          }}
        />
      </div>

      {form.isAbsence ? (
        <>
          {/* Absence fields */}
          <div>
            <Label className="text-xs">Type d'absence</Label>
            <Select value={form.type_absence} onValueChange={v => setForm(prev => ({ ...prev, type_absence: v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ABSENCE_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Heures attribuées</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number" step="0.25" min="0" max="24"
                value={form.heures_absence}
                onChange={e => setForm(prev => ({ ...prev, heures_absence: parseFloat(e.target.value) || 0 }))}
                className="h-8 text-xs"
              />
              <span className="text-xs text-slate-400">h</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">Défaut : {dureeHebdo}h / 5 = {Math.round((dureeHebdo / 5) * 100) / 100}h</p>
          </div>
        </>
      ) : (
        <>
          {/* Work fields */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Entrée</Label>
              <Input
                type="time" value={form.heure_debut}
                onChange={e => setForm(prev => ({ ...prev, heure_debut: e.target.value }))}
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Sortie</Label>
              <Input
                type="time" value={form.heure_fin}
                onChange={e => setForm(prev => ({ ...prev, heure_fin: e.target.value }))}
                className="h-8 text-xs"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Pause (minutes)</Label>
            <Input
              type="number" min="0" step="5"
              value={form.pause_minutes}
              onChange={e => setForm(prev => ({ ...prev, pause_minutes: parseInt(e.target.value) || 0 }))}
              className="h-8 text-xs"
            />
          </div>
          <div className="bg-slate-50 rounded px-2 py-1 text-center">
            <span className="text-xs text-slate-500">Heures effectives : </span>
            <span className="text-sm font-semibold text-[#0F5156]">{formatHours(heuresEffectives)}</span>
          </div>
          <div>
            <Label className="text-xs">Poste</Label>
            <Input
              value={form.poste}
              onChange={e => setForm(prev => ({ ...prev, poste: e.target.value }))}
              placeholder="Poste / fonction"
              className="h-8 text-xs"
            />
          </div>
        </>
      )}

      {/* Notes */}
      <div>
        <Label className="text-xs">Notes</Label>
        <Input
          value={form.notes}
          onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Notes..."
          className="h-8 text-xs"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 pt-1">
        <Button size="sm" onClick={onSave} className="flex-1 h-7 text-xs bg-[#0F5156] hover:bg-[#0a3d41]">
          Enregistrer
        </Button>
        {isExisting && onCopy && (
          <Button size="sm" variant="outline" onClick={onCopy} className="h-7 text-xs" title="Copier">
            <Copy className="h-3 w-3" />
          </Button>
        )}
        {isExisting && onDelete && (
          <Button size="sm" variant="destructive" onClick={onDelete} className="h-7 text-xs" title="Supprimer">
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
